import { TEXT } from "@/constants/text";

export type UserRole = "ADMIN" | "MANAGER" | "REPORTER";
export type UserStatus = "Invited" | "Active" | "Disabled";

/**
 * A user as a mutating endpoint answers: only what this service owns.
 *
 * Name, role and agency are T1's and are deliberately absent — a response
 * cannot report them without a fresh upstream read, and a copy taken before
 * the write is exactly the staleness this design removes. A screen refetches
 * the list after a mutation instead of patching a row from the response.
 */
export interface ExternalUserResponse {
  id: number;
  email: string;
  t1_user_id: number | null;
  status: string;
}

/** A list row: what we own, merged with what T1 says about the same person. */
export interface ExternalUserRowResponse extends ExternalUserResponse {
  /** Null for a user T1 could not place in an agency. */
  agency_id: number | null;
  first_name: string;
  last_name: string;
  /** Empty when T1 had nothing to say about this user. */
  role: UserRole | "";
  /** ISO-8601 UTC, or null for a user who has never signed in. */
  last_login_on: string | null;
}

/** A user as the UI holds it. */
export interface ExternalUser {
  id: number;
  email: string;
  t1UserId: number | null;
  status: UserStatus;
  agencyId: number | null;
  firstName: string;
  lastName: string;
  role: UserRole | "";
  lastLoginOn: Date | null;
}

export interface CreateExternalUserInput {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  agencyId: number;
}

/** Only the fields PATCH accepts. Email and role are refused by the API. */
export interface UpdateExternalUserInput {
  status?: UserStatus;
  agencyId?: number;
  firstName?: string;
  lastName?: string;
}

/**
 * The API's status is lowercase; the screen shows it capitalised. An unknown
 * value falls back to "Invited" rather than throwing: a status we do not
 * recognise means the API grew one, and a table that refuses to render is a
 * worse answer than a row showing the most conservative state.
 */
export const toUserStatus = (apiStatus: string): UserStatus => {
  switch (apiStatus.toLowerCase()) {
    case "active":
      return "Active";
    case "disabled":
      return "Disabled";
    default:
      return "Invited";
  }
};

export const fullName = (
  user: Pick<ExternalUser, "firstName" | "lastName">
): string => `${user.firstName} ${user.lastName}`.trim();

export const toExternalUser = (
  body: ExternalUserRowResponse
): ExternalUser => ({
  id: body.id,
  email: body.email,
  t1UserId: body.t1_user_id,
  status: toUserStatus(body.status),
  agencyId: body.agency_id,
  firstName: body.first_name,
  lastName: body.last_name,
  role: body.role,
  lastLoginOn: body.last_login_on ? new Date(body.last_login_on) : null,
});

/**
 * Last Login as the column shows it.
 *
 * "Never" is a real answer, not a missing value: T1 reports null for a user who
 * has never signed in, and an empty cell would read as data we failed to load.
 */
export const formatLastLogin = (value: Date | null): string => {
  if (value === null) return TEXT.userManagement.neverLoggedIn;
  return value.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const toCreateBody = (input: CreateExternalUserInput) => ({
  email: input.email,
  first_name: input.firstName,
  last_name: input.lastName,
  role: input.role,
  agency_id: input.agencyId,
});

/**
 * Only what changed is sent. The API rejects unknown fields outright, and a
 * null would read as "set this to nothing" rather than "leave it alone".
 */
export const toPatchBody = (patch: UpdateExternalUserInput) => {
  const body: Record<string, unknown> = {};
  if (patch.status !== undefined) body.status = patch.status;
  if (patch.agencyId !== undefined) body.agency_id = patch.agencyId;
  if (patch.firstName !== undefined) body.first_name = patch.firstName;
  if (patch.lastName !== undefined) body.last_name = patch.lastName;
  return body;
};

/** A mutation response also reports whether the invite email went out. */
export type MutationResponse = ExternalUserResponse & { invite_sent?: boolean };

/** True only when the API explicitly reports it; a missing value is not a sent invite. */
export const toInviteSent = (body: MutationResponse): boolean =>
  body.invite_sent === true;
